/**
 * Container Cash-in — bám `frmFuturesMainSubFormBank`:
 *   - Combobox Bank (Vietinbank / MBbank / BIDV, mặc định Vietinbank).
 *   - 3 nút New / Edit / Delete (Access gate bằng `SCREEN_ACCESS = 1`; FE dùng `canEdit`).
 *   - Swap subform theo bank: VTB/MBB → `FuturesBankCashInView`; BIDV → `FuturesBankCashInBIDVView`.
 */
import { Autocomplete, Button, Stack, TextField } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { useState } from 'react'

import ConfirmDialog from '../../../components/ConfirmDialog'
import { usePagePermission } from '../../../hooks/usePagePermission'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { useDeleteBankCashMutation } from '../../../store/api/futuresControlApi'
import { futuresApiErrorMessage } from '../futuresApiErrorMessage'
import BankCashInDialog, { type BankCashInRow, type BankName } from './BankCashInDialog'
import FuturesBankCashInBIDVView from './FuturesBankCashInBIDVView'
import FuturesBankCashInView from './FuturesBankCashInView'

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

const bankSelectSx: SxProps<Theme> = {
  width: 132,
  '& .MuiOutlinedInput-root': { minHeight: 26, fontSize: '0.72rem' },
  '& .MuiInputLabel-root': { fontSize: '0.7rem' },
  '& .MuiAutocomplete-input': { fontSize: '0.72rem' },
}

const BANK_OPTIONS: BankName[] = ['Vietinbank', 'MBbank', 'BIDV']

const stableKey = (r: BankCashInRow): string =>
  `${String(r.bank ?? '')}|${String(r.date ?? '').slice(0, 10)}`

export default function FuturesBankCashInTab() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  const [delBankCash, delState] = useDeleteBankCashMutation()

  const [bank, setBank] = useState<BankName>('Vietinbank')
  const [selected, setSelected] = useState<BankCashInRow | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogRow, setDialogRow] = useState<BankCashInRow | null>(null)
  const [pendingDelete, setPendingDelete] = useState<BankCashInRow | null>(null)

  const onSelect = (row: BankCashInRow | null) => setSelected(row)

  const openNew = () => {
    setDialogRow(null)
    setDialogOpen(true)
  }
  const openEdit = () => {
    if (!selected) return
    setDialogRow(selected)
    setDialogOpen(true)
  }
  const askDelete = () => {
    if (!selected?.date) return
    setPendingDelete(selected)
  }

  const confirmDelete = async () => {
    if (!pendingDelete?.date) return
    try {
      await delBankCash({
        date: String(pendingDelete.date).slice(0, 10),
        bank: String(pendingDelete.bank ?? bank),
      }).unwrap()
      snack.success('Successfully Deleted')
      setSelected(null)
      setPendingDelete(null)
    } catch (err) {
      snack.error(futuresApiErrorMessage(err))
    }
  }

  const actions = canEdit ? (
    <>
      <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} onClick={openNew}>
        New
      </Button>
      <Button
        variant="outlined"
        size="small"
        color="warning"
        sx={actionToolbarBtnSx}
        disabled={!selected}
        onClick={openEdit}
      >
        Edit
      </Button>
      <Button
        variant="outlined"
        size="small"
        color="error"
        sx={actionToolbarBtnSx}
        disabled={!selected}
        onClick={askDelete}
      >
        Delete
      </Button>
    </>
  ) : null

  // Stack header với bank selector + slot actions.
  const headerActions = (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
      <Autocomplete
        size="small"
        autoHighlight
        disableClearable
        options={BANK_OPTIONS}
        value={bank}
        onChange={(_, v) => {
          setBank(v)
          setSelected(null)
        }}
        isOptionEqualToValue={(a, b) => a === b}
        sx={bankSelectSx}
        renderInput={(params) => <TextField {...params} size="small" label="Bank" />}
      />
      {actions}
    </Stack>
  )

  const selectedKey = selected ? stableKey(selected) : null

  return (
    <>
      {bank === 'BIDV' ? (
        <FuturesBankCashInBIDVView
          bank="BIDV"
          selectedKey={selectedKey}
          onSelectionChange={onSelect}
          actionsSlot={headerActions}
        />
      ) : (
        <FuturesBankCashInView
          bank={bank}
          selectedKey={selectedKey}
          onSelectionChange={onSelect}
          actionsSlot={headerActions}
        />
      )}

      <BankCashInDialog
        open={dialogOpen}
        bank={bank}
        row={dialogRow}
        onClose={() => setDialogOpen(false)}
        onSaved={() => setSelected(null)}
        canEdit={canEdit}
      />

      <ConfirmDialog
        open={pendingDelete != null}
        title="Delete Bank Cash-in"
        message={
          pendingDelete
            ? `Do you want to delete cash of bank = '${pendingDelete.bank ?? bank}' with timing = '${String(pendingDelete.date ?? '').slice(0, 10)}' ?`
            : ''
        }
        danger
        loading={delState.isLoading}
        cancelLabel="Cancel"
        confirmLabel="Delete"
        onCancel={() => setPendingDelete(null)}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
      />
    </>
  )
}
